import React from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Mail, CheckCircle, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface EmailConfirmationAlertProps {
  email?: string;
  type?: 'info' | 'success';
}

export const EmailConfirmationAlert = ({ email, type = 'info' }: EmailConfirmationAlertProps) => {
  const { toast } = useToast();
  const [isResending, setIsResending] = React.useState(false);

  const resendConfirmation = async () => {
    if (!email) return;
    
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        toast({
          title: "Erro ao reenviar",
          description: "Não foi possível reenviar o email de confirmação. Tente novamente.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email reenviado",
          description: "Um novo email de confirmação foi enviado para sua caixa de entrada.",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro inesperado ao reenviar email.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  if (type === 'success') {
    return (
      <Alert className="bg-green-50 border-green-200 mb-4">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          Email confirmado com sucesso! Você pode fazer login agora.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="bg-blue-50 border-blue-200 mb-4">
      <Mail className="h-4 w-4 text-blue-600" />
      <AlertDescription className="text-blue-800">
        <div className="flex flex-col space-y-3">
          <div>
            {email ? (
              <>
                Um email de confirmação foi enviado para <strong>{email}</strong>. 
                Verifique sua caixa de entrada e clique no link para ativar sua conta.
              </>
            ) : (
              "Verifique sua caixa de entrada para o email de confirmação."
            )}
          </div>
          {email && (
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={resendConfirmation}
                disabled={isResending}
                className="text-blue-600 border-blue-300 hover:bg-blue-100"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Reenviando...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Reenviar email
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};